import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { User } from '../../../../../services/endpoints/people/user';
import { useGetAllNewsItemsQuery } from '../../../../../services/endpoints/website/newsItem';
import { getCurrentUser } from '../../../../../services/helper';
import { NewsItem } from '../../../../../types/website.types';
import { newsItemColumns } from '../../../../../utils/consts/columnDefs/websiteColumns';
import { getCreatedUpdatedAtFromTo } from '../../../../../utils/consts/filterFields/dates';
import { getImageFilterFields } from '../../../../../utils/consts/filterFields/image';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';

const defaultScope = '';
const currentUser = getCurrentUser();

export const userCanEditCreateDelete_NewsItems = (user: User | null) => {
  if (user?.isSuper || user?.isSupremeAdmin || user?.isWebContentEditor) {
    return true;
  }

  return false;
};

export const userCanView_NewsItems = (user: User | null) => {
  if (user?.isSuper || user?.isSupremeAdmin || user?.isWebContentEditor || user?.isGlobal) {
    return true;
  }

  return false;
};

export const NewsItemsView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });
  const { data, error, isLoading, isFetching } = useGetAllNewsItemsQuery(filters);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const navigate: NavigateFunction = useNavigate();
  const isGlobalUser = currentUser?.isGlobal;

  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'details',
      label: 'Details',
      value: getFilterValue(filters.data?.filterBy, 'details')
    },
    ...getImageFilterFields(filters.data?.filterBy),
    {
      name: 'published_on_from',
      id: 'published_on',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="published_on_from"
          label="Published On"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'published_on', 'greater_equal_than')
    },
    {
      name: 'published_on_to',
      id: 'published_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="published_on_to" />,
      value: getFilterValue(filters.data?.filterBy, 'published_on', 'lesser_equal_than')
    },
    ...getCreatedUpdatedAtFromTo(filters.data?.filterBy)
  ];

  if (!isGlobalUser) {
    filterFormFields.splice(4, 1);
  }

  useEffect(() => {
    if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setNewsItems(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`News Items`}
        columns={newsItemColumns}
        data={newsItems}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        isLoading={isLoading}
        isFetching={isFetching}
        indexDownloadPath="news-items"
        filters={filters?.data?.filterBy}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        newItemEl={
          userCanEditCreateDelete_NewsItems(currentUser) && (
            <SimpleButton
              variant="sfc-blueberry"
              height={40}
              truncate={false}
              onClick={() => navigate('/admin/website/news-items/new')}
            >
              New News Item
            </SimpleButton>
          )
        }
        onFiltersChanged={setFilters}
      />
    </div>
  );
};
